import { ApiQueryParams } from '@frontend/api-utils';
import { UserGroupClient } from '@frontend/group/api';
import { SettingKey, SettingValue } from '@frontend/setting/types';
import { TransactionUserClient } from '@frontend/transaction/api';
import { TransactionUser, TransactionUserType } from '@frontend/transaction/types';
import { UserClient } from '@frontend/user/api';
import { IUser, UserListResponse, UserQueryParams } from '@frontend/user/types';
import { User } from '@frontend/user/utils';
import { ScreenTrigger, ScreenType } from '@frontend/wizard/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { syncTransactionUsers } from '../../utils/sync-wizard-transaction-data';
import { useScreen } from '../useScreen';

const toBooleanSetting = (value: SettingValue | undefined): boolean => value === true || value === 'true' || value === 1 || value === '1';

const DELAYED_RECEIVERS_MIN_SEARCH_CHARACTERS = 3;

interface ViewProps {
    isLoading: boolean;
    receivers: IUser[] | null;
    selectedReceivers: (string | null)[];
    receiverSubsets: (string[] | undefined)[];
    delayedReceiversSearch: boolean;
    fetchReceiverOptions: (queryParams?: ApiQueryParams<UserQueryParams>) => Promise<UserListResponse>;
    addReceiver: () => void;
    removeReceiver: (index: number) => void;
    updateReceiver: (index: number, receiverId: string | null) => void;
    submitDisabled: boolean;
    onSubmit: () => void;
    onBack: (type: ScreenType) => void;
}

const useSelectReceivers = (props: { wizard_id: string }): ViewProps => {
    const { store, onBack, updateWizardData, onSubmit: navigateToNextStep } = useScreen(props.wizard_id);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [receivers, setReceivers] = useState<IUser[] | null>(null);
    const [selectedReceivers, setSelectedReceivers] = useState<(string | null)[]>(() => {
        const receiverIds = ((store?.data?.receivers as TransactionUser[]) || [])
            .map((receiver) => receiver.user_id)
            .filter((userId): userId is string => !!userId);
        return receiverIds.length > 0 ? receiverIds : [null];
    });
    const selectedReceiversRef = useRef(selectedReceivers);

    const delayedReceiversSearch = toBooleanSetting(store?.functionalitySettings?.find((fs) => fs.key === SettingKey.DELAYED_RECEIVERS_SEARCH)?.value);

    useEffect(() => {
        selectedReceiversRef.current = selectedReceivers;
    }, [selectedReceivers]);

    useEffect(() => {
        const receiverUserGroupId = store?.functionalitySettings?.find((fs) => fs.key === SettingKey.OPEN_DROP_OFF_RECEIVERS_USER_GROUP_ID)?.value;

        const loadReceivers = async () => {
            try {
                if (receiverUserGroupId && typeof receiverUserGroupId === 'string') {
                    const response = await UserGroupClient.fetchUserGroupMembers({ group_id: receiverUserGroupId });
                    const userResponse = await UserClient.resolveUsers(response.results.map((member) => member.entity_id));
                    setReceivers(userResponse.results);
                } else {
                    setReceivers(null);
                }

                if (store?.data.transaction?.id) {
                    const fetchedReceivers = await TransactionUserClient.fetchTransactionUsers({
                        transaction_id: store.data.transaction.id,
                        type: TransactionUserType.RECEIVER
                    });

                    if (fetchedReceivers.results.length > 0) {
                        updateWizardData({
                            receivers: fetchedReceivers.results
                        });
                        setSelectedReceivers(fetchedReceivers.results.map((receiver) => receiver.user_id));
                    }
                }
            } finally {
                setIsLoading(false);
            }
        };

        loadReceivers();
    }, []);

    const addReceiver = () => {
        setSelectedReceivers([...selectedReceiversRef.current, null]);
    };

    const removeReceiver = (index: number) => {
        setSelectedReceivers(selectedReceiversRef.current.filter((_, i) => i !== index));
    };

    const updateReceiver = useCallback((index: number, receiverId: string | null) => {
        const updated = [...selectedReceiversRef.current];
        updated[index] = receiverId;
        setSelectedReceivers(updated);
    }, []);

    const receiverSubsets = useMemo(
        () =>
            selectedReceivers.map((receiverId) =>
                receivers
                    ?.filter((receiver) => {
                        const found = selectedReceivers.find((id) => id === receiver.id);
                        if (found) {
                            return receiverId === found;
                        }
                        return true;
                    })
                    .map((receiver) => receiver.id)
            ),
        [receivers, selectedReceivers]
    );

    const fetchReceiverOptions = useCallback(
        (queryParams?: ApiQueryParams<UserQueryParams>): Promise<UserListResponse> => {
            const search = typeof queryParams?.search === 'string' ? queryParams.search.trim().toLowerCase() : '';

            if (delayedReceiversSearch && search.length < DELAYED_RECEIVERS_MIN_SEARCH_CHARACTERS) {
                return Promise.resolve({ count: 0, results: [] });
            }

            let filtered = receivers ?? [];

            if (search) {
                filtered = filtered.filter((user) => {
                    const haystack = [new User(user).getDisplayName(), user.unique_identifier, user.email ?? '', user.mobile ?? ''].join(' ').toLowerCase();
                    return haystack.includes(search);
                });
            }

            const index = Number.parseInt(String(queryParams?.index ?? '0'), 10);
            const size = Number.parseInt(String(queryParams?.size ?? '100'), 10);

            return Promise.resolve({
                count: filtered.length,
                results: filtered.slice(index * size, (index + 1) * size)
            });
        },
        [receivers, delayedReceiversSearch]
    );

    const canContinue = selectedReceivers.some((receiver) => receiver !== null);

    const onSubmit = async () => {
        if (!store || !canContinue || !store.data.transaction?.id) return;

        setIsLoading(true);
        try {
            const accountId = store.wizard.account_id;
            const transactionId = store.data.transaction.id;
            const relevantReceivers = selectedReceiversRef.current.filter((receiver): receiver is string => receiver !== null);
            const syncedReceivers = await syncTransactionUsers(accountId, transactionId, TransactionUserType.RECEIVER, relevantReceivers);

            updateWizardData({
                receivers: syncedReceivers
            });

            const nextStep = store.steps.find((step) => step.screen === ScreenType.SELECT_RECEIVERS && step.trigger === ScreenTrigger.SUBMIT);
            navigateToNextStep(nextStep);
        } finally {
            setIsLoading(false);
        }
    };

    return {
        isLoading,
        receivers,
        selectedReceivers,
        receiverSubsets,
        delayedReceiversSearch,
        fetchReceiverOptions,
        addReceiver,
        removeReceiver,
        updateReceiver,
        submitDisabled: !canContinue || !store?.data.transaction || isLoading,
        onSubmit,
        onBack
    };
};

export default useSelectReceivers;
